import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Table, Select, SelectOption, Spinner, Button } from 'ui';
import useQueryApiClient from 'utils/useQueryApiClient';
import { Product, QualityMarkProduct } from 'interfaces/BackendModels';
import { StyledQualityMarksTable } from '../style';
import { useAuth } from 'hooks/useAuth';
import { DeleteOutlined } from '@ant-design/icons';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';

const QualityMarks = ({ stableFarmId }: { stableFarmId?: number }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [products, setProducts] = useState<Product[] | null>(null);
  const [formUpdateKey, setFormUpdateKey] = useState(0);
  const [showProducerColumn, setShowProducerColumn] = useState(false);

  const intl = useIntl();
  const form = useFormInstance();
  const { user, can } = useAuth();
  const farmId = stableFarmId ? stableFarmId : user?.farmId;

  const handleFormValuesChange = () => {
    setFormUpdateKey((prev) => prev + 1);
  };

  useQueryApiClient({
    request: {
      url: 'api/v2/qmark/farms/' + farmId + '/products',
      data: {
        page: 1,
        page_size: 100,
        filter: {
          farm: farmId,
        },
      },
    },
    enabled: !!farmId,
    onSuccess: (response) => {
      setProducts(response);
      setIsLoading(false);
    },
    onError: () => {
      setIsLoading(false);
    },
  });

  const getSelectCount = (
    qualityMarkProductId: number,
    initialProducts?: Product[]
  ) => {
    const formValues = form.getFieldValue([
      'qualityMarks',
      String(qualityMarkProductId),
    ]);
    if (!formValues || typeof formValues !== 'object') {
      return initialProducts?.length
        ? Math.max(0, initialProducts.length - 1)
        : 0;
    }

    const keys = Object.keys(formValues).map((key) => Number(key));
    const maxIndex = keys.length > 0 ? Math.max(...keys) : 0;

    return maxIndex;
  };

  const getAvailableProducts = (
    qualityMarkProductId: number,
    selectIndex: number,
    currentValue?: number | string
  ) => {
    if (!products) return [];

    const formValues = form.getFieldValue([
      'qualityMarks',
      String(qualityMarkProductId),
    ]);
    const selectedValues = new Set<number>();

    if (formValues && typeof formValues === 'object') {
      Object.entries(formValues).forEach(([key, value]) => {
        const idx = Number(key);
        if (idx !== selectIndex && value != null && value !== '') {
          selectedValues.add(Number(value));
        }
      });
    }

    return products.filter(
      (product) =>
        !selectedValues.has(product.id) || product.id === currentValue
    );
  };

  const hasSelected = (qualityMarkProductId: number, count: number) => {
    const formValues = form.getFieldValue([
      'qualityMarks',
      String(qualityMarkProductId),
    ]);
    if (!formValues || typeof formValues !== 'object') return false;

    for (let i = 0; i <= count; i++) {
      if (formValues[i] != null && formValues[i] !== '') return true;
    }
    return false;
  };

  const addSelect = (qualityMarkProductId: number) => {
    const currentCount = getSelectCount(qualityMarkProductId);
    const nextIndex = currentCount + 1;
    form.setFieldValue(
      ['qualityMarks', String(qualityMarkProductId), nextIndex],
      undefined
    );
    handleFormValuesChange();
  };

  const removeSelect = (qualityMarkProductId: number, index: number) => {
    const formValues =
      form.getFieldValue(['qualityMarks', String(qualityMarkProductId)]) || {};

    const remainingEntries = Object.entries(formValues)
      .filter(([key]) => Number(key) !== index)
      .sort((a, b) => Number(a[0]) - Number(b[0]));

    const newValues: Record<number, number | string | undefined> = {};
    remainingEntries.forEach(([, value], idx) => {
      newValues[idx] = value as number | string | undefined;
    });

    if (Object.keys(newValues).length === 0) {
      newValues[0] = undefined;
    }

    form.setFieldValue(
      ['qualityMarks', String(qualityMarkProductId)],
      newValues
    );
    handleFormValuesChange();
  };

  const initializeFormData = (data: QualityMarkProduct[]) => {
    if (!data || data.length === 0) return;

    const formData: Record<string, Record<number, number>> = {};

    data.forEach((qualityProduct) => {
      if (qualityProduct.is_own === false) return;

      const productsArray = qualityProduct?.products || [];
      if (productsArray.length > 0) {
        const productMap: Record<number, number> = {};
        productsArray.forEach((product, index) => {
          if (product?.id) {
            productMap[index] = product.id;
          }
        });

        formData[String(qualityProduct.id)] = productMap;
      }
    });

    form.setFieldsValue({ qualityMarks: formData });
  };

  const renderProductSelects = (qualityMarkProduct: QualityMarkProduct) => {
    const qmId = qualityMarkProduct.id;
    const productsArray = qualityMarkProduct.products || [];

    if (qualityMarkProduct.is_own === false) {
      return (
        <div className={'product-selects-container'}>
          {productsArray.map((product) => (
            <div key={product.id}>{product.name}</div>
          ))}
        </div>
      );
    }

    const count = getSelectCount(qmId, productsArray);
    const showAddButton = hasSelected(qmId, count);

    const renderSelect = (index: number) => {
      const currentValue = form.getFieldValue([
        'qualityMarks',
        String(qmId),
        index,
      ]);
      const availableProducts = getAvailableProducts(qmId, index, currentValue);

      return (
        <div key={`${qmId}_${index}`} className={'select-with-remove'}>
          <Select
            name={['qualityMarks', String(qmId), index]}
            allowClear
            disabled={!can('edit_quality_mark')}
            noStyle
            onChange={() => handleFormValuesChange()}
          >
            {availableProducts.map((product: Product) => (
              <SelectOption key={product.id} value={product.id}>
                {product.name}
              </SelectOption>
            ))}
          </Select>
          {(index > 0 || (currentValue != null && currentValue !== '')) && (
            <div className={'remove-button-wrapper'}>
              <Button
                type={'text'}
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeSelect(qmId, index)}
              />
            </div>
          )}
        </div>
      );
    };

    const renderAdditionalSelects = () => {
      const selects = [];
      for (let i = 1; i <= count; i++) {
        selects.push(renderSelect(i));
      }
      return selects;
    };

    return (
      <div key={formUpdateKey} className={'product-selects-container'}>
        {renderSelect(0)}
        {renderAdditionalSelects()}
        {showAddButton && (
          <div className={'add-button-wrapper'}>
            <Button
              label={intl.formatMessage({ id: 'general.add' })}
              onClick={() => addSelect(qmId)}
              type={'default'}
            />
          </div>
        )}
      </div>
    );
  };

  const columns = [
    {
      title: intl.formatMessage({ id: 'quality_marks_mapping.title' }),
      width: showProducerColumn ? '30%' : '50%',
      render: (qualityMarkProduct: QualityMarkProduct) => (
        <>
          <div className={'bold'}>{qualityMarkProduct.quality_mark.name}</div>
          <div>{qualityMarkProduct.registry_name}</div>
        </>
      ),
    },
    ...(showProducerColumn
      ? [
          {
            title: intl.formatMessage({
              id: 'quality_marks_mapping.producer_title',
            }),
            width: '25%',
            render: (qualityMarkProduct: QualityMarkProduct) =>
              qualityMarkProduct.producer_name,
          },
        ]
      : []),
    {
      title: intl.formatMessage({
        id: 'quality_marks_mapping.product_title',
      }),
      width: showProducerColumn ? '45%' : '50%',
      render: renderProductSelects,
    },
  ];

  return (
    <>
      <Spinner spinning={isLoading}>
        <div>
          {intl.formatMessage({ id: 'quality_marks_mapping.info-message' })}
        </div>
        <StyledQualityMarksTable>
          <Table
            url={'api/v2/qmark/farms/' + farmId + '/qualityProducts'}
            columns={columns}
            disablePagination
            saveResponse={(response: QualityMarkProduct[]) => {
              const data = response;
              if (data && Array.isArray(data)) {
                setShowProducerColumn(data.some((row) => row.is_own === false));
                initializeFormData(data);
              } else {
              }
            }}
          />
        </StyledQualityMarksTable>
      </Spinner>
    </>
  );
};

export default QualityMarks;
